import { GameType } from "@aura-frontend/omnibus-az-client";
import { toast } from "@win-reality/react-ui";

import { EventsTable } from "@/components/EventsTable";
import { MachineSelector } from "@/components/MachineSelector";
import { AlertMessage } from "@/components/ui/AlertMessage";
import { useDateRange } from "@/hooks/useDateRange";
import { useDownloadGameCsv } from "@/hooks/useDownloadGameCsv";
import { useEventFilters } from "@/hooks/useEventFilters";
import { useGamesFromMachine } from "@/hooks/useGamesFromMachine";
import { useMachineSelection } from "@/hooks/useMachineSelection";

const FindCSV = () => {
  const {
    selectedEventType,
    setSelectedEventType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    endTime,
    resetFilters,
  } = useEventFilters();

  const {
    selectedRegion,
    selectedMachineId,
    handleRegionChange,
    handleMachineChange,
  } = useMachineSelection({
    includeRegion: true,
    onRegionChange: () => {
      resetFilters();
    },
    onMachineChange: () => {
      resetFilters();
    },
  });

  const { downloadCsv, downloadingGameId } = useDownloadGameCsv({
    onError: (msg) => toast(msg, { variant: "error" }),
  });

  const {
    games,
    isLoading: loadingGames,
    error: gamesError,
  } = useGamesFromMachine({
    machineUuid: selectedMachineId || undefined,
    gameType:
      selectedEventType !== "all" ? (selectedEventType as GameType) : undefined,
    startTime,
    endTime,
  });

  const { dateRange, handleDateRangeChange } = useDateRange({
    initialFrom: startDate || undefined,
    initialTo: endDate || undefined,
    onFromChange: (v) => setStartDate(v ?? ""),
    onToChange: (v) => setEndDate(v ?? ""),
  });

  return (
    <div className="relative flex flex-col">
      <div className="px-5 mt-8">
        <MachineSelector
          showRegionSelector={true}
          selectedRegion={selectedRegion}
          selectedMachineId={selectedMachineId}
          onRegionChange={handleRegionChange}
          onMachineChange={handleMachineChange}
          isTaggingMachines={false}
        />
      </div>

      {selectedMachineId && (
        <div className="px-5 mt-6">
          <h2 className="text-xl font-semibold mb-4 px-5">Events</h2>
          {gamesError ? (
            <AlertMessage
              variant="destructive"
              title="Unable to load events"
              description="There was a problem loading events for the selected machine. Please try selecting a different machine or refresh the page."
            />
          ) : (
            <EventsTable
              games={games ?? []}
              isLoading={loadingGames}
              onDownload={downloadCsv}
              downloadingGameId={downloadingGameId}
              selectedEventType={selectedEventType}
              onEventTypeChange={setSelectedEventType}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export { FindCSV };
